import { useCallback } from 'react';

import type { ProductKeys3P } from '../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import { onOAuthCompleted, onOAuthStarted } from '../../../common/utils/events';
import { type OAuthSource, onOAuthFailed } from '../../../common/utils/events/oauth-events';
import { isProductImpersonated } from '../../../common/utils/search-result-utils';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../analytics';

type OAuthEventProps = {
	cloudId: string;
	product: string;
	isOnSearchDialog?: boolean;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
};

export const useFireOAuthStarted = ({
	cloudId,
	product,
	isOnSearchDialog,
	thirdPartyConfigs,
}: OAuthEventProps) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const callback = useCallback(
		(oAuthSource: OAuthSource) => {
			fireAnalyticsEvent(
				onOAuthStarted({
					cloudId,
					product,
					commonAttributes,
					nonPrivacySafeAttributes,
					isOnSearchDialog,
					oAuthSource,
					isImpersonated: isProductImpersonated(product as ProductKeys3P, thirdPartyConfigs),
				}),
			);
		},
		[
			fireAnalyticsEvent,
			cloudId,
			product,
			commonAttributes,
			nonPrivacySafeAttributes,
			isOnSearchDialog,
			thirdPartyConfigs,
		],
	);
	return callback;
};

export const useFireOAuthCompleted = ({
	cloudId,
	product,
	isOnSearchDialog,
	thirdPartyConfigs,
}: OAuthEventProps) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const callback = useCallback(
		(oAuthSource: OAuthSource) => {
			fireAnalyticsEvent(
				onOAuthCompleted({
					cloudId,
					product,
					commonAttributes,
					nonPrivacySafeAttributes,
					isOnSearchDialog,
					oAuthSource,
					isImpersonated: isProductImpersonated(product as ProductKeys3P, thirdPartyConfigs),
				}),
			);
		},
		[
			fireAnalyticsEvent,
			cloudId,
			product,
			commonAttributes,
			nonPrivacySafeAttributes,
			isOnSearchDialog,
			thirdPartyConfigs,
		],
	);
	return callback;
};

export const useFireOAuthFailed = ({
	cloudId,
	product,
	isOnSearchDialog,
	thirdPartyConfigs,
}: OAuthEventProps) => {
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const callback = useCallback(
		(oAuthSource: OAuthSource) => {
			fireAnalyticsEvent(
				onOAuthFailed({
					cloudId,
					product,
					commonAttributes,
					nonPrivacySafeAttributes,
					isOnSearchDialog,
					oAuthSource,
					isImpersonated: isProductImpersonated(product as ProductKeys3P, thirdPartyConfigs),
				}),
			);
		},
		[
			fireAnalyticsEvent,
			cloudId,
			product,
			commonAttributes,
			nonPrivacySafeAttributes,
			isOnSearchDialog,
			thirdPartyConfigs,
		],
	);
	return callback;
};
